import { FormikProvider, useFormik } from "formik";
import { useCart, useShop, useUser } from "hooks";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { clear } from "store/cart/actions";
import { API_ENDPOINTS, request } from "utilities";
import { loadScript, toJson } from "utilities/str";
import notification from "utilities/notification";
import DefaultAddressQuery from "./DefaultAddressQuery";
import SelectedAddressQuery from "./SelectedAddressQuery";

const OrderForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { items, subTotal, total, discount, coupon } = useCart();
  const { isUserLoggedIn, user } = useUser();
  const { info: shop } = useShop();
  const { data: selectedAddress } = SelectedAddressQuery(); // User-selected address
  const { data: defaultAddress } = DefaultAddressQuery(); // Default address
  const address = toJson(selectedAddress?.address || defaultAddress?.address || "{}");

  const [payMethod, setPayMethod] = useState("");
  const [shippingCharge, setShippingCharge] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);

  // Disable "Proceed to Payment" button if user is not logged in or cart is empty
  const isDisabled = isUserLoggedIn && items.length > 0;

  // Calculate shipping charges and grand total
  useEffect(() => {
    let myTotal = total - discount;
    if (myTotal <= 0) myTotal = 0;

    // Handle Shipping Charges
    let shippingCost = 0;
    if (shop.shipping_charges_enabled === 1) {
      if (shop.free_shipping_limit && myTotal >= shop.free_shipping_limit) {
        shippingCost = 0; // Free shipping
      } else {
        shippingCost = parseFloat(shop.shipping_charges) || 0;
      }
    }

    setShippingCharge(shippingCost);
    setGrandTotal(myTotal);
  }, [total, discount, shop]);

  // Check COD Availability
  const isCODAvailable =
    shop.cod_enabled === 1 &&
    (shop.cod_limit === null || grandTotal <= Number(shop.cod_limit));

  // COD Charges (applied only if COD is available and selected)
  const codCharge =
    isCODAvailable && shop.cod_charges && payMethod === "CASH_ON_DELIVERY"
      ? Number(shop.cod_charges)
      : 0;

  // Final Grand Total with Shipping & COD Charges
  const finalTotal = grandTotal + shippingCharge + codCharge;

  // Form initial values
  const initialValues = {
    products: items.map((item) => ({
      order_quantity: item?.quantity,
      product_id: item?.product_id,
      unit_price: item?.price,
      subtotal: item?.price * item?.quantity,
    })),
    status: 1,
    amount: total,
    shipping_charges: shippingCharge,
    paid_total: finalTotal,
    shop_id: shop?.id,
    total: finalTotal,
    discount,
    customer_contact: user?.contact || "0123456789",
    customer_id: user?.id,
    customer_email: user?.email,
    payment_gateway: payMethod,
    billing_address: address,
    shipping_address: address,
    coupon_id: coupon?.id,
  };

  // Form submission handler
  const onSubmit = async (values) => {
    if (!payMethod) {
      notification("error", "Please select a payment method");
      return;
    }
    if (
      !values.billing_address ||
      !values.shipping_address ||
      Object.keys(values.billing_address).length === 0 ||
      Object.keys(values.shipping_address).length === 0
    ) {
      notification("error", "Please select a billing and shipping address");
      return;
    }
    if (payMethod === "CASH_ON_DELIVERY" && !isCODAvailable) {
      notification("error", "Cash on Delivery is not available for this order");
      return;
    }
    if (payMethod === "razorPay") {
      // Handle Razorpay payment
      await displayRazorpay(values);
    } else {
      // Handle other payment methods (e.g., COD)
      request.post(API_ENDPOINTS.ORDER, values).then((response) => {
        if (response?.success) {
          notification("success", response?.message);
          dispatch(clear());
          navigate(`/`);
        } else {
          notification("error", response?.message);
        }
      });
    }
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit,
  });

  // Razorpay payment handler
  async function displayRazorpay(order) {
    const res = await loadScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!res) {
      notification("error", "Razorpay SDK failed to load. Are you online?");
      return;
    }
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      currency: process.env.REACT_APP_RAZORPAY_CURRENCY,
      amount: grandTotal * 100,
      name: "BEAUTE INDIA",
      description: "LUX" + Date.now(),
      handler: function (response) {
        if (response?.razorpay_payment_id) {
          const mydata = {
            paymentId: response?.razorpay_payment_id,
            amount: grandTotal * 100,
            currency: "INR",
          };
          request
            .post(API_ENDPOINTS.PAYMENT_VERIFY, mydata)
            .then((presponse) => {
              const myRes = JSON.parse(presponse.data);

              if (myRes?.status === "captured" && myRes?.captured === true) {
                request
                  .post(API_ENDPOINTS.ORDER, order)
                  .then((responsedata) => {
                    if (responsedata?.success) {
                      notification("success", responsedata?.message);
                      dispatch(clear());
                      navigate(`/`);
                    } else {
                      notification("error", responsedata?.message);
                    }
                  });
              } else {
                notification("error", "Payment Failed Try Again!!!");
              }
            });
        } else {
          notification("error", "Payment Failed Try Again!!!");
        }
      },
      prefill: {
        name: user.name,
        email: user.email,
      },
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  }

  return (
    <Fragment>
      <FormikProvider value={formik}>
        <div className="payment-option-section">
          <div className="row pt-5 pb-5">
            <div className="col-lg-8">
              <h2 className="payment">Payment Options</h2>
              <div className="choose-payment-mode mt-5 pe-5">
                <ul>
                  {/* Razorpay Option */}
                  <div className="radio-btn-section pe-3">
                    <li>Razorpay</li>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="payment_gateway"
                        value="razorPay"
                        checked={payMethod === "razorPay"}
                        onChange={(e) => setPayMethod(e.currentTarget.value)}
                      />
                    </div>
                  </div>

                  {/* Cash on Delivery Option */}
                  {isCODAvailable && (
                    <>
                      <hr />
                      <div className="radio-btn-section pe-3">
                        <li>Cash on Delivery</li>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="payment_gateway"
                            value="CASH_ON_DELIVERY"
                            checked={payMethod === "CASH_ON_DELIVERY"}
                            onChange={(e) =>
                              setPayMethod(e.currentTarget.value)
                            }
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <hr />
                </ul>
              </div>
            </div>

            {/* Order Summary Section */}
            <div className="col-lg-4">
              <div className="total-section">
                <h1 className="mb-5" style={{ fontSize: "26px" }}>
                  <u>Total</u>
                </h1>

                <div className="total-products">
                  {items.map((item, idx) => (
                    <div className="product-price" key={idx}>
                      <h5 style={{ fontSize: "18px" }}>{item?.model?.name}</h5>
                      <span style={{ fontSize: "18px" }}>
                        ₹ {item?.price * item?.quantity}
                      </span>
                    </div>
                  ))}

                  <div className="product-price">
                    <h5>Discount</h5>
                    <span>₹ {discount}</span>
                  </div>

                  <div className="product-price">
                    <h5>Sub Total</h5>
                    <span>₹ {grandTotal}</span>
                  </div>

                  <div className="product-price">
                    <h5>Shipping Charges</h5>
                    <span>₹ {shippingCharge}</span>
                  </div>

                  {/* Show COD Charges only when COD is selected */}
                  {isCODAvailable && payMethod === "CASH_ON_DELIVERY" && (
                    <div className="product-price">
                      <h5>COD Charges</h5>
                      <span>₹ {codCharge}</span>
                    </div>
                  )}

                  <div className="product-price">
                    <h5>
                      <strong>Final Total</strong>
                    </h5>
                    <span>
                      <strong>₹ {finalTotal}</strong>
                    </span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!isDisabled}
                  onClick={formik.handleSubmit}
                  className="payment-btn mt-5"
                >
                  Proceed to Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      </FormikProvider>
    </Fragment>
  );
};

export default OrderForm;